import React, { useEffect, useMemo, useState } from "react";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import { config } from "@/config/config.ts";
import liveScoringHeroBg from "@/landing/assets/live_scoring_hero_bg.webp";
import LandingNav from "@/landing/components/LandingNav.tsx";
import FooterSection from "@/landing/sections/FooterSection.tsx";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/americano-mixer")({
  component: RouteComponent,
});

type Side = "Left" | "Right";

type EliminationTeam = {
  id: string;
  seed: number;
  left: PlayerStats;
  right?: PlayerStats;
  name: string;
};

type KnockoutMatch = {
  id: string;
  title: string;
  roundTitle: string;
  teamA: EliminationTeam | null;
  teamB: EliminationTeam | null;
};

type Player = {
  id: number;
  name: string;
  side: Side;
  group: number;
};

type Team = {
  id: string;
  left: Player;
  right: Player;
  name: string;
};

type Fixture = {
  id: string;
  group: number | "Cross Group";
  sourceGroups?: [number, number];
  round: number;
  court: number | "Fixture 5";
  teamA: Team;
  teamB: Team;
  isCrossGroup: boolean;
};

type Score = {
  a?: number | "";
  b?: number | "";
};

type PlayerStats = Player & {
  played: number;
  wins: number;
  losses: number;
  gf: number;
  ga: number;
  diff: number;
};

type UnmatchedPartnership = {
  id: string;
  group: number;
  round: number;
  team: Team;
};

const defaultPlayers: Player[] = [
  { id: 1, name: "Letts", side: "Left", group: 1 },
  { id: 2, name: "Neil", side: "Left", group: 1 },
  { id: 3, name: "Richard", side: "Left", group: 1 },
  { id: 4, name: "Smerds", side: "Left", group: 1 },
  { id: 5, name: "Ryan S", side: "Left", group: 1 },

  { id: 6, name: "Luther", side: "Left", group: 2 },
  { id: 7, name: "Greg", side: "Left", group: 2 },
  { id: 8, name: "Kemp", side: "Left", group: 2 },
  { id: 9, name: "Shazzy", side: "Left", group: 2 },
  { id: 10, name: "Jon B", side: "Left", group: 2 },

  { id: 11, name: "Sean D", side: "Right", group: 1 },
  { id: 12, name: "Gale", side: "Right", group: 1 },
  { id: 13, name: "Jo", side: "Right", group: 1 },
  { id: 14, name: "Spargs", side: "Right", group: 1 },
  { id: 15, name: "Fred", side: "Right", group: 1 },

  { id: 16, name: "Tatey", side: "Right", group: 2 },
  { id: 17, name: "Shaun", side: "Right", group: 2 },
  { id: 18, name: "Oli", side: "Right", group: 2 },
  { id: 19, name: "Kyle", side: "Right", group: 2 },
  { id: 20, name: "Alex", side: "Right", group: 2 },
];

function RouteComponent() {
  return (
    <div className="min-h-screen bg-[#EDEEF4]">
      <JsonLdBreadcrumbList
        routeId={Route.id}
        crumbs={[
          {
            name: "Americano Mixer",
            item: "https://scorepal.co/americano-mixer/",
          },
        ]}
      />

      <title>Americano Mixer Tournament Format | ScorePal</title>
      <meta
        name="description"
        content="Run an Americano Mixer tournament with ScorePal — rotating partners, automatic scheduling, live scoring, and instant leaderboards for padel, pickleball and more."
      />
      <link rel="canonical" href={`${config.publicUrl}/americano-mixer/`} />

      <section className="relative overflow-hidden p-4">
        <div
          className="flex flex-col min-h-screen justify-between relative pb-16 rounded-4xl bg-[#EDEEF4] bg-top bg-cover bg-no-repeat saturate-[0.87]"
          style={{ backgroundImage: `url(${liveScoringHeroBg})` }}
        >
          <div className="absolute inset-0 bg-linear-to-r from-black/60 via-black/30 to-transparent rounded-4xl" />

          <div className="relative z-20">
            <LandingNav />
          </div>

          <div className="container mx-auto px-6 lg:px-12 pt-8 flex-1 flex flex-col justify-center relative z-10">
            <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-4">
              Americano Mixer
            </h1>

            <p className="font-inter text-white/90 text-base lg:text-xl mb-8 max-w-2xl leading-relaxed">
              Run a fast, social tournament where partners rotate every round.
              Automatic scheduling, live scoring, and real-time leaderboards —
              all in ScorePal.
            </p>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-6 lg:px-12 py-12">
        <ScorePalTournament />
      </section>

      <FooterSection />
    </div>
  );
}

function ScorePalTournament() {
  const isLeagueDisplay =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("view") === "leagues";

  const storageKey = "scorepal-americano-mixer-v1";

  const [tab, setTab] = useState<"setup" | "fixtures" | "leagues" | "elimination">(
    isLeagueDisplay ? "leagues" : "setup",
  );

  const [players, setPlayers] = useState<Player[]>(defaultPlayers);
  const [groupCount, setGroupCount] = useState(2);
  const [courtCount, setCourtCount] = useState(5);
  const [progressCount, setProgressCount] = useState(8);
  const [scores, setScores] = useState<Record<string, Score>>({});
  const [generated, setGenerated] = useState(false);
  const [koWinners, setKoWinners] = useState<Record<string, string>>({});

  const leftPlayers = players.filter((player) => player.side === "Left");
  const rightPlayers = players.filter((player) => player.side === "Right");

  const saveState = () => {
    if (typeof window === "undefined") return;

    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        players,
        groupCount,
        courtCount,
        progressCount,
        scores,
        generated,
      }),
    );
  };

  const loadState = () => {
    if (typeof window === "undefined") return;

    const saved = window.localStorage.getItem(storageKey);
    if (!saved) return;

    try {
      const data = JSON.parse(saved) as {
        players?: Player[];
        groupCount?: number;
        courtCount?: number;
        progressCount?: number;
        scores?: Record<string, Score>;
        generated?: boolean;
      };

      if (data.players) setPlayers(data.players);
      if (data.groupCount) setGroupCount(data.groupCount);
      if (data.courtCount) setCourtCount(data.courtCount);
      if (data.progressCount) setProgressCount(data.progressCount);
      if (data.scores) setScores(data.scores);
      if (typeof data.generated === "boolean") setGenerated(data.generated);
    } catch {
      // Ignore bad saved state.
    }
  };

  useEffect(() => {
    loadState();
  }, []);

  useEffect(() => {
    saveState();
  }, [players, groupCount, courtCount, progressCount, scores, generated]);

  useEffect(() => {
    if (!isLeagueDisplay) return;

    const interval = window.setInterval(loadState, 1000);
    return () => window.clearInterval(interval);
  }, [isLeagueDisplay]);

  const resetTournament = () => {
    setGenerated(false);
    setScores({});
    setKoWinners({});
  };

  const resetToDefaults = () => {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(storageKey);
    }

    setPlayers(defaultPlayers);
    setGroupCount(2);
    setCourtCount(5);
    setProgressCount(8);
    setScores({});
    setKoWinners({});
    setGenerated(false);
    setTab("setup");
  };

  const addPlayer = (side: Side) => {
    setPlayers((current) => [
      ...current,
      {
        id: Date.now(),
        name: "",
        side,
        group: 1,
      },
    ]);

    resetTournament();
  };

  const updatePlayer = (
    id: number,
    field: keyof Pick<Player, "name" | "side" | "group">,
    value: string | number,
  ) => {
    setPlayers((current) =>
      current.map((player) =>
        player.id === id ? { ...player, [field]: value } : player,
      ),
    );

    resetTournament();
  };

  const removePlayer = (id: number) => {
    setPlayers((current) => current.filter((player) => player.id !== id));
    resetTournament();
  };

  const autoGenerateGroups = () => {
    const left = players.filter((player) => player.side === "Left");
    const right = players.filter((player) => player.side === "Right");

    setPlayers((current) =>
      current.map((player) => {
        const list = player.side === "Left" ? left : right;
        const index = list.findIndex((item) => item.id === player.id);

        return {
          ...player,
          group: (index % Math.max(groupCount, 1)) + 1,
        };
      }),
    );

    resetTournament();
  };

  const makeTeamsForRound = (
    left: Player[],
    right: Player[],
    roundIndex: number,
  ): Team[] => {
    const size = Math.min(left.length, right.length);

    return Array.from({ length: size }, (_, index) => {
      const rightPlayer = right[(index + roundIndex) % size];

      return {
        id: `${left[index].id}-${rightPlayer.id}`,
        left: left[index],
        right: rightPlayer,
        name: `${left[index].name} + ${rightPlayer.name}`,
      };
    });
  };

  const buildGroupRound = (
    group: number,
    left: Player[],
    right: Player[],
    courts: number,
  ): { fixtures: Fixture[]; unmatched: UnmatchedPartnership[] } => {
    const size = Math.min(left.length, right.length);
    const fixtures: Fixture[] = [];
    const unmatched: UnmatchedPartnership[] = [];

    if (size < 2) {
      return { fixtures, unmatched };
    }

    const matchesPerRound = Math.min(Math.floor(size / 2), courts);

    for (let round = 0; round < size; round++) {
      const teams = makeTeamsForRound(left, right, round);

      const unmatchedIndex = round % size;
      const unmatchedTeam = teams[unmatchedIndex];

      unmatched.push({
        id: `g${group}-r${round + 1}-unmatched`,
        group,
        round: round + 1,
        team: unmatchedTeam,
      });

      const availableTeams = teams.filter((_, index) => index !== unmatchedIndex);

      for (let index = 0; index < matchesPerRound; index++) {
        const teamA = availableTeams[index];
        const teamB = availableTeams[availableTeams.length - 1 - index];

        if (!teamA || !teamB) continue;
        if (teamA.id === teamB.id) continue;

        fixtures.push({
          id: `g${group}-r${round + 1}-m${index + 1}`,
          group,
          round: round + 1,
          court: index + 1,
          teamA,
          teamB,
          isCrossGroup: false,
        });
      }
    }

    return { fixtures, unmatched };
  };

  const fixtures = useMemo<Fixture[]>(() => {
    if (!generated) return [];

    let normalFixtures: Fixture[] = [];
    let unmatched: UnmatchedPartnership[] = [];

    for (let group = 1; group <= groupCount; group++) {
      const groupPlayers = players.filter((player) => player.group === group);
      const left = groupPlayers.filter((player) => player.side === "Left");
      const right = groupPlayers.filter((player) => player.side === "Right");

      const result = buildGroupRound(group, left, right, courtCount);

      normalFixtures = [...normalFixtures, ...result.fixtures];
      unmatched = [...unmatched, ...result.unmatched];
    }

    const crossGroupFixtures: Fixture[] = [];

    const byRound = unmatched.reduce<Record<number, UnmatchedPartnership[]>>(
      (acc, item) => {
        acc[item.round] ||= [];
        acc[item.round].push(item);
        return acc;
      },
      {},
    );

    Object.entries(byRound).forEach(([round, roundUnmatched]) => {
      const remaining = [...roundUnmatched].sort((a, b) => a.group - b.group);
      let fixtureIndex = 1;

      while (remaining.length > 1) {
        const a = remaining.shift();
        if (!a) break;

        const matchIndex = remaining.findIndex((candidate) => {
          if (candidate.group === a.group) return false;

          const aIds = [a.team.left.id, a.team.right.id];
          const bIds = [candidate.team.left.id, candidate.team.right.id];

          return !aIds.some((id) => bIds.includes(id));
        });

        if (matchIndex === -1) continue;

        const b = remaining.splice(matchIndex, 1)[0];

        crossGroupFixtures.push({
          id: `cross-r${round}-${fixtureIndex}-g${a.group}-g${b.group}`,
          group: "Cross Group",
          sourceGroups: [a.group, b.group],
          round: Number(round),
          court: "Fixture 5",
          teamA: a.team,
          teamB: b.team,
          isCrossGroup: true,
        });

        fixtureIndex += 1;
      }
    });

    return [...normalFixtures, ...crossGroupFixtures];
  }, [players, groupCount, courtCount, generated]);

  const updateScore = (fixtureId: string, side: "a" | "b", value: string) => {
    setScores((current) => ({
      ...current,
      [fixtureId]: {
        ...(current[fixtureId] || {}),
        [side]: value === "" ? "" : Number(value),
      },
    }));

    setKoWinners({});
  };

  const standings = useMemo(() => {
    const table: Record<number, PlayerStats> = {};

    players.forEach((player) => {
      table[player.id] = {
        ...player,
        played: 0,
        wins: 0,
        losses: 0,
        gf: 0,
        ga: 0,
        diff: 0,
      };
    });

    fixtures.forEach((fixture) => {
      const score = scores[fixture.id];

      if (
        !score ||
        score.a === "" ||
        score.b === "" ||
        score.a === undefined ||
        score.b === undefined ||
        score.a === score.b
      ) {
        return;
      }

      const teamAPlayers = [
        table[fixture.teamA.left.id],
        table[fixture.teamA.right.id],
      ];

      const teamBPlayers = [
        table[fixture.teamB.left.id],
        table[fixture.teamB.right.id],
      ];

      teamAPlayers.forEach((player) => {
        player.played += 1;
        player.gf += Number(score.a);
        player.ga += Number(score.b);
      });

      teamBPlayers.forEach((player) => {
        player.played += 1;
        player.gf += Number(score.b);
        player.ga += Number(score.a);
      });

      if (Number(score.a) > Number(score.b)) {
        teamAPlayers.forEach((player) => {
          player.wins += 1;
        });

        teamBPlayers.forEach((player) => {
          player.losses += 1;
        });
      } else {
        teamBPlayers.forEach((player) => {
          player.wins += 1;
        });

        teamAPlayers.forEach((player) => {
          player.losses += 1;
        });
      }

      [...teamAPlayers, ...teamBPlayers].forEach((player) => {
        player.diff = player.gf - player.ga;
      });
    });

    const result: Record<number, { left: PlayerStats[]; right: PlayerStats[] }> =
      {};

    const sortPlayers = (rows: PlayerStats[]) =>
      [...rows].sort(
        (a, b) =>
          b.wins - a.wins ||
          b.gf - a.gf ||
          b.diff - a.diff ||
          a.name.localeCompare(b.name),
      );

    for (let group = 1; group <= groupCount; group++) {
      const groupPlayers = players
        .filter((player) => player.group === group)
        .map((player) => table[player.id]);

      result[group] = {
        left: sortPlayers(groupPlayers.filter((player) => player.side === "Left")),
        right: sortPlayers(groupPlayers.filter((player) => player.side === "Right")),
      };
    }

    return result;
  }, [players, fixtures, scores, groupCount]);

  const rankedEliminationPlayers = useMemo(() => {
    const perSide = Math.floor(progressCount / 2);
    const left: PlayerStats[] = [];
    const right: PlayerStats[] = [];

    Object.values(standings).forEach((group) => {
      left.push(...group.left);
      right.push(...group.right);
    });

    const sortPlayers = (rows: PlayerStats[]) =>
      [...rows].sort(
        (a, b) =>
          b.wins - a.wins ||
          b.gf - a.gf ||
          b.diff - a.diff ||
          a.name.localeCompare(b.name),
      );

    return {
      left: sortPlayers(left).slice(0, perSide),
      right: sortPlayers(right).slice(0, perSide),
    };
  }, [standings, progressCount]);

  const qualifiedPlayers = useMemo(
    () => [...rankedEliminationPlayers.left, ...rankedEliminationPlayers.right],
    [rankedEliminationPlayers],
  );

  const eliminationTeams = useMemo<EliminationTeam[]>(() => {
    return rankedEliminationPlayers.left.map((leftPlayer, index) => {
      const rightPlayer = rankedEliminationPlayers.right[index];

      return {
        id: `team-${leftPlayer.id}-${rightPlayer?.id ?? "tbc"}`,
        seed: index + 1,
        left: leftPlayer,
        right: rightPlayer,
        name: `${leftPlayer.name} + ${rightPlayer?.name ?? "TBC"}`,
      };
    });
  }, [rankedEliminationPlayers]);

  const bracketSeedOrder = (
    teams: EliminationTeam[],
  ): [EliminationTeam | null, EliminationTeam | null][] => {
    if (teams.length === 4) {
      return [
        [teams[0] ?? null, teams[3] ?? null],
        [teams[1] ?? null, teams[2] ?? null],
      ];
    }

    if (teams.length === 8) {
      return [
        [teams[0] ?? null, teams[7] ?? null],
        [teams[3] ?? null, teams[4] ?? null],
        [teams[1] ?? null, teams[6] ?? null],
        [teams[2] ?? null, teams[5] ?? null],
      ];
    }

    const matches: [EliminationTeam | null, EliminationTeam | null][] = [];

    for (let index = 0; index < teams.length / 2; index++) {
      matches.push([
        teams[index] ?? null,
        teams[teams.length - 1 - index] ?? null,
      ]);
    }

    return matches;
  };

  const knockoutRounds = useMemo<KnockoutMatch[][]>(() => {
    if (eliminationTeams.length < 2) return [];

    const firstRoundPairs = bracketSeedOrder(eliminationTeams);

    const firstRoundTitle =
      eliminationTeams.length >= 8
        ? "Quarter Finals"
        : eliminationTeams.length === 4
          ? "Semi Finals"
          : "Elimination Round";

    const firstRound: KnockoutMatch[] = firstRoundPairs.map(
      ([teamA, teamB], index): KnockoutMatch => ({
        id: `ko-r1-m${index + 1}`,
        title:
          firstRoundTitle === "Quarter Finals"
            ? `QF${index + 1}`
            : firstRoundTitle === "Semi Finals"
              ? `SF${index + 1}`
              : `Match ${index + 1}`,
        roundTitle: firstRoundTitle,
        teamA,
        teamB,
      }),
    );

    const rounds: KnockoutMatch[][] = [firstRound];

    let previousRound = firstRound;
    let roundNumber = 2;

    while (previousRound.length > 1) {
      const matches: KnockoutMatch[] = [];

      for (let index = 0; index < previousRound.length; index += 2) {
        const matchNumber = Math.floor(index / 2) + 1;
        const isFinal = previousRound.length === 2;
        const roundTitle = isFinal ? "Final" : "Semi Finals";

        const previousA = previousRound[index];
        const previousB = previousRound[index + 1];

        const winnerAId = koWinners[previousA.id];
        const winnerBId = previousB ? koWinners[previousB.id] : "";

        const winnerA =
          eliminationTeams.find((team) => team.id === winnerAId) ?? null;

        const winnerB =
          eliminationTeams.find((team) => team.id === winnerBId) ?? null;

        matches.push({
          id: `ko-r${roundNumber}-m${matchNumber}`,
          title: isFinal ? "Final" : `SF${matchNumber}`,
          roundTitle,
          teamA: winnerA,
          teamB: winnerB,
        });
      }

      rounds.push(matches);
      previousRound = matches;
      roundNumber += 1;
    }

    return rounds;
  }, [eliminationTeams, koWinners]);

  const fixturesByRoundGroup = useMemo(() => {
    return fixtures.reduce<Record<number, Record<string, Fixture[]>>>(
      (acc, fixture) => {
        const groupKey = fixture.isCrossGroup
          ? "Cross Group Fixture 5"
          : `Group ${fixture.group}`;

        acc[fixture.round] ||= {};
        acc[fixture.round][groupKey] ||= [];
        acc[fixture.round][groupKey].push(fixture);

        return acc;
      },
      {},
    );
  }, [fixtures]);

  const groupSummaries = useMemo(() => {
    return Array.from({ length: groupCount }, (_, index) => {
      const group = index + 1;
      const groupPlayers = players.filter((player) => player.group === group);
      const left = groupPlayers.filter((player) => player.side === "Left");
      const right = groupPlayers.filter((player) => player.side === "Right");
      const size = Math.min(left.length, right.length);

      return {
        group,
        left: left.length,
        right: right.length,
        rounds: size,
        groupFixturesPerRound: Math.floor(size / 2),
        crossGroupFixture: size % 2 === 1,
      };
    });
  }, [players, groupCount]);

  return (
    <div className="rounded-3xl bg-white p-6 shadow-xl">
      {!isLeagueDisplay && (
        <>
          <h2 className="text-3xl font-bold mb-4">ScorePal Americano Mixer</h2>

          <div className="flex flex-wrap gap-2 mb-6">
            {(["setup", "fixtures", "leagues", "elimination"] as const).map(
              (item) => (
                <button
                  key={item}
                  type="button"
                  className={`rounded-xl px-4 py-2 font-bold ${
                    tab === item ? "bg-black text-white" : "bg-gray-100 text-black"
                  }`}
                  onClick={() => setTab(item)}
                >
                  {item[0].toUpperCase() + item.slice(1)}
                </button>
              ),
            )}
          </div>
        </>
      )}

      {isLeagueDisplay && (
        <div className="mb-6 rounded-2xl bg-black p-6 text-white">
          <h2 className="text-3xl font-bold">Live League Display</h2>
          <p className="text-white/80">Updates from the scoring screen.</p>
        </div>
      )}

      {!isLeagueDisplay && tab === "setup" && (
        <section>
          <div className="grid gap-4 md:grid-cols-4 mb-6">
            <StatCard label="Players" value={players.length} />
            <StatCard label="Left" value={leftPlayers.length} />
            <StatCard label="Right" value={rightPlayers.length} />
            <StatCard label="Groups" value={groupCount} />
          </div>

          <div className="grid gap-4 md:grid-cols-3 mb-6">
            <label className="font-bold">
              Groups
              <input
                className="mt-2 w-full rounded-xl border p-3"
                type="number"
                min={1}
                value={groupCount}
                onChange={(event) => {
                  setGroupCount(Number(event.target.value));
                  resetTournament();
                }}
              />
            </label>

            <label className="font-bold">
              Courts
              <input
                className="mt-2 w-full rounded-xl border p-3"
                type="number"
                min={1}
                value={courtCount}
                onChange={(event) => {
                  setCourtCount(Number(event.target.value));
                  resetTournament();
                }}
              />
            </label>

            <label className="font-bold">
              Progress to elimination
              <input
                className="mt-2 w-full rounded-xl border p-3"
                type="number"
                min={2}
                step={2}
                value={progressCount}
                onChange={(event) => {
                  setProgressCount(Number(event.target.value));
                  setKoWinners({});
                }}
              />
            </label>
          </div>

          <div className="mb-6 rounded-2xl bg-gray-50 p-4">
            <h3 className="text-xl font-bold mb-3">Tournament Summary</h3>

            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="border p-2">Group</th>
                  <th className="border p-2">Left</th>
                  <th className="border p-2">Right</th>
                  <th className="border p-2">Rounds</th>
                  <th className="border p-2">Group fixtures / round</th>
                  <th className="border p-2">Cross-group Fixture 5</th>
                </tr>
              </thead>

              <tbody>
                {groupSummaries.map((summary) => (
                  <tr key={summary.group}>
                    <td className="border p-2">Group {summary.group}</td>
                    <td className="border p-2">{summary.left}</td>
                    <td className="border p-2">{summary.right}</td>
                    <td className="border p-2">{summary.rounds}</td>
                    <td className="border p-2">
                      {summary.groupFixturesPerRound}
                    </td>
                    <td className="border p-2">
                      {summary.crossGroupFixture ? "Yes" : "No"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            <button
              type="button"
              className="rounded-xl bg-blue-600 px-4 py-2 font-bold text-white"
              onClick={autoGenerateGroups}
            >
              Auto Generate Groups
            </button>

            <button
              type="button"
              className="rounded-xl bg-gray-800 px-4 py-2 font-bold text-white"
              onClick={resetToDefaults}
            >
              Reset to 20 Test Players
            </button>

            <button
              type="button"
              className="rounded-xl bg-black px-4 py-2 font-bold text-white"
              onClick={() => {
                setGenerated(true);
                setTab("fixtures");
              }}
            >
              Generate Tournament
            </button>

            <button
              type="button"
              className="rounded-xl bg-green-700 px-4 py-2 font-bold text-white"
              onClick={() => {
                saveState();
                window.open(`${window.location.pathname}?view=leagues`, "_blank");
              }}
            >
              Open League Display
            </button>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <PlayerEditor
              title="Left Players"
              side="Left"
              players={leftPlayers}
              groupCount={groupCount}
              onAdd={addPlayer}
              onRemove={removePlayer}
              onUpdate={updatePlayer}
            />

            <PlayerEditor
              title="Right Players"
              side="Right"
              players={rightPlayers}
              groupCount={groupCount}
              onAdd={addPlayer}
              onRemove={removePlayer}
              onUpdate={updatePlayer}
            />
          </div>
        </section>
      )}

      {!isLeagueDisplay && tab === "fixtures" && (
        <section>
          <h3 className="text-2xl font-bold mb-4">Fixtures</h3>

          {!generated && (
            <p className="rounded-xl bg-yellow-50 p-4 text-yellow-900">
              Generate the tournament from the setup tab first.
            </p>
          )}

          {Object.entries(fixturesByRoundGroup).map(([round, groups]) => (
            <div key={round} className="mb-8 rounded-2xl border p-4">
              <h4 className="text-xl font-bold mb-4">Round {round}</h4>

              {Object.entries(groups).map(([groupLabel, roundFixtures]) => (
                <div key={groupLabel} className="mb-6 rounded-2xl bg-gray-50 p-4">
                  <h5 className="text-lg font-bold mb-4">{groupLabel}</h5>

                  <div className="grid gap-4 md:grid-cols-2">
                    {roundFixtures.map((fixture) => (
                      <FixtureCard
                        key={fixture.id}
                        fixture={fixture}
                        score={scores[fixture.id]}
                        onScoreChange={updateScore}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </section>
      )}

      {(tab === "leagues" || isLeagueDisplay) && (
        <section>
          <h3 className="text-2xl font-bold mb-4">League Tables</h3>

          {Object.entries(standings).map(([group, sides]) => (
            <div key={group} className="mb-8 rounded-2xl border p-4">
              <h4 className="text-xl font-bold">Group {group}</h4>

              <div className="grid gap-6 md:grid-cols-2">
                <LeagueTable title="Left League" rows={sides.left} />
                <LeagueTable title="Right League" rows={sides.right} />
              </div>
            </div>
          ))}
        </section>
      )}

      {!isLeagueDisplay && tab === "elimination" && (
        <section>
          <h3 className="text-2xl font-bold mb-4">Elimination</h3>

          {!generated && (
            <p className="rounded-xl bg-yellow-50 p-4 text-yellow-900 mb-6">
              Generate the tournament from the setup tab first.
            </p>
          )}

          <div className="grid gap-6 md:grid-cols-2 mb-8">
            <LeagueTable
              title={`Left Rankings - Top ${rankedEliminationPlayers.left.length}`}
              rows={rankedEliminationPlayers.left}
            />

            <LeagueTable
              title={`Right Rankings - Top ${rankedEliminationPlayers.right.length}`}
              rows={rankedEliminationPlayers.right}
            />
          </div>

          <h4 className="text-xl font-bold mb-2">Elimination Teams</h4>

          <div className="grid gap-3 md:grid-cols-2 mb-8">
            {eliminationTeams.map((team) => (
              <div key={team.id} className="rounded-xl border p-3">
                <strong>Seed {team.seed}</strong>: {team.name}
              </div>
            ))}
          </div>

          <h4 className="text-xl font-bold mb-4">Bracket</h4>

          {knockoutRounds.length === 0 && (
            <p className="rounded-xl bg-gray-50 p-4 text-gray-700">
              Add scores and make sure enough players qualify to create the bracket.
            </p>
          )}

          <div className="grid gap-6 lg:grid-cols-3">
            {knockoutRounds.map((round, roundIndex) => (
              <div key={roundIndex} className="rounded-2xl border p-4">
                <h5 className="text-lg font-bold mb-4">
                  {round[0]?.roundTitle ?? `Round ${roundIndex + 1}`}
                </h5>

                <div className="grid gap-4">
                  {round.map((match) => (
                    <div key={match.id} className="rounded-xl border bg-gray-50 p-4">
                      <strong>{match.title}</strong>

                      <p className="mt-2">
                        {match.teamA
                          ? `(${match.teamA.seed}) ${match.teamA.name}`
                          : "TBC"}
                      </p>

                      <p className="font-bold">vs</p>

                      <p>
                        {match.teamB
                          ? `(${match.teamB.seed}) ${match.teamB.name}`
                          : "TBC"}
                      </p>

                      <select
                        className="mt-3 w-full rounded border p-2"
                        value={koWinners[match.id] ?? ""}
                        onChange={(event) =>
                          setKoWinners((current) => ({
                            ...current,
                            [match.id]: event.target.value,
                          }))
                        }
                        disabled={!match.teamA || !match.teamB}
                      >
                        <option value="">Select winner</option>

                        {match.teamA && (
                          <option value={match.teamA.id}>
                            {match.teamA.name}
                          </option>
                        )}

                        {match.teamB && (
                          <option value={match.teamB.id}>
                            {match.teamB.name}
                          </option>
                        )}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl bg-black p-4 text-white">
      <div className="text-sm opacity-70">{label}</div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}

function PlayerEditor({
  title,
  side,
  players,
  groupCount,
  onAdd,
  onRemove,
  onUpdate,
}: {
  title: string;
  side: Side;
  players: Player[];
  groupCount: number;
  onAdd: (side: Side) => void;
  onRemove: (id: number) => void;
  onUpdate: (
    id: number,
    field: keyof Pick<Player, "name" | "side" | "group">,
    value: string | number,
  ) => void;
}) {
  return (
    <div className="rounded-2xl border p-4">
      <h3 className="text-xl font-bold mb-4">
        {title} ({players.length})
      </h3>

      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="border p-2">Name</th>
            <th className="border p-2">Group</th>
            <th className="border p-2">Remove</th>
          </tr>
        </thead>

        <tbody>
          {players.map((player) => (
            <tr key={player.id}>
              <td className="border p-2">
                <input
                  className="w-full rounded border p-2"
                  value={player.name}
                  onChange={(event) =>
                    onUpdate(player.id, "name", event.target.value)
                  }
                />
              </td>

              <td className="border p-2">
                <select
                  className="w-full rounded border p-2"
                  value={player.group}
                  onChange={(event) =>
                    onUpdate(player.id, "group", Number(event.target.value))
                  }
                >
                  {Array.from({ length: groupCount }, (_, index) => (
                    <option key={index + 1} value={index + 1}>
                      Group {index + 1}
                    </option>
                  ))}
                </select>
              </td>

              <td className="border p-2">
                <button
                  type="button"
                  className="rounded bg-red-600 px-3 py-1 text-white"
                  onClick={() => onRemove(player.id)}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <button
        type="button"
        className="mt-4 rounded-xl bg-blue-600 px-4 py-2 font-bold text-white"
        onClick={() => onAdd(side)}
      >
        Add {side} Player
      </button>
    </div>
  );
}

function FixtureCard({
  fixture,
  score,
  onScoreChange,
}: {
  fixture: Fixture;
  score?: Score;
  onScoreChange: (fixtureId: string, side: "a" | "b", value: string) => void;
}) {
  return (
    <div className="rounded-xl border bg-white p-4">
      <strong>
        {fixture.isCrossGroup
          ? "Cross Group Fixture 5"
          : `Group ${fixture.group} Court ${fixture.court}`}
      </strong>

      <div className="mt-3 grid gap-3 md:grid-cols-[1fr_auto_1fr] md:items-center">
        <div className="rounded-xl border border-green-300 bg-green-50 p-3 font-bold text-green-900">
          {fixture.teamA.name}
        </div>

        <div className="font-bold">vs</div>

        <div className="rounded-xl border border-orange-300 bg-orange-50 p-3 font-bold text-orange-900">
          {fixture.teamB.name}
        </div>
      </div>

      {fixture.isCrossGroup && fixture.sourceGroups && (
        <p className="mt-3 text-sm text-gray-600">
          Counts toward Group {fixture.sourceGroups[0]} and Group{" "}
          {fixture.sourceGroups[1]} league tables.
        </p>
      )}

      <div className="mt-4 flex items-center gap-2">
        <strong>Score</strong>

        <input
          className="w-20 rounded border p-2"
          type="number"
          placeholder="A"
          value={score?.a ?? ""}
          onChange={(event) =>
            onScoreChange(fixture.id, "a", event.target.value)
          }
        />

        <span>-</span>

        <input
          className="w-20 rounded border p-2"
          type="number"
          placeholder="B"
          value={score?.b ?? ""}
          onChange={(event) =>
            onScoreChange(fixture.id, "b", event.target.value)
          }
        />
      </div>
    </div>
  );
}

function LeagueTable({
  title,
  rows,
}: {
  title: string;
  rows: PlayerStats[];
}) {
  return (
    <div>
      <h5 className="font-bold mt-4">{title}</h5>

      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="border p-2">Rank</th>
            <th className="border p-2">Player</th>
            <th className="border p-2">P</th>
            <th className="border p-2">W</th>
            <th className="border p-2">L</th>
            <th className="border p-2">GF</th>
            <th className="border p-2">GA</th>
            <th className="border p-2">+/-</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((player, index) => (
            <tr key={player.id}>
              <td className="border p-2">{index + 1}</td>
              <td className="border p-2">{player.name}</td>
              <td className="border p-2">{player.played}</td>
              <td className="border p-2">{player.wins}</td>
              <td className="border p-2">{player.losses}</td>
              <td className="border p-2">{player.gf}</td>
              <td className="border p-2">{player.ga}</td>
              <td className="border p-2">{player.diff}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
